import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Receipt as ReceiptIcon, ScrollText, User, Building } from "lucide-react";
import { toast } from "sonner";

import { formatDate, formatDateTime } from "@/lib/datetime";
/** Both customer & franchise ledgers use this shared component. */
export default function LedgerPage({ payerType }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const endpoint = payerType === "customer" ? `/sales/customers/${id}/ledger` : `/sales/franchises/${id}/ledger`;
  const printPath = payerType === "customer" ? `/sales/customers/${id}/ledger/print` : `/sales/franchises/${id}/ledger/print`;
  const listPath = payerType === "customer" ? "/sales/customers" : "/sales/branches";
  const Icon = payerType === "customer" ? User : Building;

  const load = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get(endpoint, { params });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [endpoint, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-6 text-sm text-gray-500">Loading ledger…</div>;

  const ent = data.entity || {};
  const filterActive = dateFrom || dateTo;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => nav(listPath)} className="gap-1.5" data-testid="ledger-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <Icon className="h-6 w-6 text-[#9B1C1C]" />
        <div>
          <h2 className="font-heading font-bold text-lg text-gray-900 leading-tight">{ent.name}</h2>
          <div className="text-xs text-gray-500">
            {payerType === "customer" ? "Customer" : "Franchise"} Ledger · <span className="font-mono">{ent.code}</span>
            {ent.gstin && <span> · GSTIN {ent.gstin}</span>}
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <a href={`${printPath}${filterActive ? `?${new URLSearchParams({ ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }) })}` : ""}`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="gap-1.5" data-testid="ledger-print"><Printer className="h-4 w-4" /> Print Statement</Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label="Opening Balance" value={data.opening_balance_in_range} tone="text-gray-700" />
        <SummaryCard label="Total Debits" value={data.entries.reduce((s, e) => s + Number(e.debit || 0), 0)} tone="text-blue-700" />
        <SummaryCard label="Total Credits" value={data.entries.reduce((s, e) => s + Number(e.credit || 0), 0)} tone="text-emerald-700" />
        <SummaryCard label="Closing Balance" value={data.closing_balance} tone="text-[#9B1C1C]" bold />
      </div>

      <Surface padded={false}>
        <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          <ScrollText className="h-4 w-4 text-gray-500" />
          <h3 className="font-heading font-semibold text-sm">Ledger Entries</h3>
          <div className="ml-auto flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="ledger-from" />
            <span className="text-xs text-gray-400">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="ledger-to" />
            {filterActive && <Button size="sm" variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="ledger-clear">Clear</Button>}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
              <th className="px-3 py-2 w-24">Date</th>
              <th className="px-3 py-2 w-32">Ref #</th>
              <th className="px-3 py-2">Narration</th>
              <th className="px-3 py-2 w-28 text-right">Debit</th>
              <th className="px-3 py-2 w-28 text-right">Credit</th>
              <th className="px-3 py-2 w-28 text-right">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.entries.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-gray-400">No entries in this period.</td></tr>
            )}
            {data.entries.map((e, i) => (
              <tr key={i} className="hover:bg-gray-50" data-testid={`ledger-row-${i}`}>
                <td className="px-3 py-2 text-xs">{e.date}</td>
                <td className="px-3 py-2 font-mono text-xs font-semibold">{e.ref_no}</td>
                <td className="px-3 py-2 text-xs">{e.narration}</td>
                <td className="px-3 py-2 text-right tabular-nums text-xs">{e.debit ? formatINR(e.debit) : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums text-xs text-emerald-700">{e.credit ? formatINR(e.credit) : "—"}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold ${e.balance > 0.005 ? "text-red-700" : e.balance < -0.005 ? "text-emerald-700" : "text-gray-500"}`}>{formatINR(e.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Surface>

      {data.outstanding_invoices.length > 0 && (
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2">
            <ReceiptIcon className="h-4 w-4 text-red-600" />
            <h3 className="font-heading font-semibold text-sm">Outstanding Invoices</h3>
            <span className="ml-auto text-xs font-semibold text-red-700">Total: {formatINR(data.total_outstanding)}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Invoice #</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2 text-right">Grand Total</th>
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.outstanding_invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50" data-testid={`ledger-out-${inv.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{inv.invoice_no}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(inv.invoice_date)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(inv.grand_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-500">{formatINR(inv.allocated)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700">{formatINR(inv.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone = "", bold = false }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`text-lg tabular-nums mt-0.5 ${tone} ${bold ? "font-heading font-bold text-xl" : "font-semibold"}`}>{formatINR(value || 0)}</div>
    </div>
  );
}
